import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ProfessionalService } from '../../../shared/services/professional.service';
import { FileService } from '../../../shared/services/file.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogHistoryProfessionalComponent } from '../dialog-history-professional/dialog-history-professional.component';
import { DialogChangePasswordProfessionalComponent } from '../dialog-change-password-professional/dialog-change-password-professional.component';

export interface IProfessionals {
  id: string;
  _id: string;
  name: string;
  fullname: string;
  image: string;
  cellphone: string;
  email: string;
  dateOfBirth: string;
  address: string;
  isChecked: boolean;
  isBlocked: boolean;
  isActive: boolean;
}

@Component({
  selector: 'app-list-professionals',
  templateUrl: './list-professionals.component.html',
  styleUrls: ['./list-professionals.component.scss'],
})
export class ListProfessionalsComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private statusFilter = '';
  private searchTerm = '';
  private searchSubject = new Subject<string>();
  isLoading = false;
  photoUrls: Record<string, SafeUrl> = {};
  previewPhotoSrc: SafeUrl | string | null = null;

  totalProfessionals = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 50];

  displayedColumns: string[] = [
    'action',
    'image',
    'name',
    'document',
    'email',
    'licenseNumber',
    'cellphone',
    'status',
  ];

  dataSource: MatTableDataSource<IProfessionals>;

  constructor(
    private professionalService: ProfessionalService,
    private fileService: FileService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe((term) => {
      this.searchTerm = term;
      this.pageIndex = 0;
      this.getAll(this.statusFilter);
    });

    this.route.queryParams.subscribe((params: any) => {
      this.statusFilter = params?.status;
      this.getAll(params?.status);
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchSubject.next(filterValue.trim());
  }

  goToUserEdit(id: string) {
    this.router.navigate(['/professionals', id]);
  }

  goToScale(id: string) {
    this.router.navigate(['/professionals/scale', id]);
  }

  viewHistory(professionalId: string, name: string) {
    this.dialog.open(DialogHistoryProfessionalComponent, { data: {professionalId, name} });
  }

  changePassword(professionalId: string, name: string) {
    this.dialog.open(DialogChangePasswordProfessionalComponent, { data: {professionalId, name} });
  }

  downloadRegisterForm(id: string, name: string) {
    this.snackbar.open('Por favor, aguarde enquanto geramos o documento', 'OK', { duration: 3000 });

    this.professionalService.registerFormById(id).subscribe(
      (file: Blob) => {
        this.snackbar.open('Gerado com sucesso', 'OK', { duration: 3000 });

        const url = window.URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ficha_cadastral_${name}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      },
      () =>
        this.snackbar.open(
          'Não foi possível baixar a ficha de cadastro, tente novamente mais tarde!',
          'OK',
          { duration: 3000 }
        )
    );
  }

  deleteById(id: string) {
    this.professionalService.deleteById(id).subscribe(
      () => {
        this.snackbar.open('deletado com sucesso', 'OK', { duration: 3000 });
        this.getAll();
      },
      () =>
        this.snackbar.open(
          'Não foi possível deletar esse médico, tente novamente mais tarde!',
          'OK',
          { duration: 3000 }
        )
    );
  }

  toggleActive(id: string, currentIsActive: boolean) {
    const newIsActive = !currentIsActive;
    this.professionalService.toggleActive(id, newIsActive).subscribe({
      next: () => {
        this.snackbar.open(
          newIsActive ? 'Profissional ativado com sucesso' : 'Profissional desativado com sucesso',
          'OK',
          { duration: 3000 }
        );
        this.getAll(this.statusFilter);
      },
      error: () => {
        this.snackbar.open('Não foi possível alterar o status do profissional', 'OK', { duration: 3000 });
      },
    });
  }

  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.getAll(this.statusFilter);
  }

  getAll(status?: string) {
    this.isLoading = true;
    this.photoUrls = {};
    this.professionalService.getAll(status, this.pageIndex + 1, this.pageSize, this.searchTerm || undefined).subscribe((result: any) => {
      this.totalProfessionals = result.total ?? 0;
      const professionals = result.professionals.map((professional: any) => ({
        id: professional._id,
        name: professional.fullname,
        document: professional.document,
        email: professional.email,
        licenseNumber: professional.licenseNumber,
        cellphone: professional.cellphone,
        status: professional.status,
        createdAt: professional.createdAt,
        action: 'ri-more-2-fill',
        histories: professional.histories,
        photoFileId: professional.photoFileId ?? null,
        isActive: professional.isActive !== false,
      }));
      this.dataSource = new MatTableDataSource<IProfessionals>(professionals);
      this.isLoading = false;

      professionals.forEach((p: any) => {
        if (p.photoFileId) {
          this.fileService.getImageUrl(p.photoFileId)
            .pipe(catchError(() => of(null)))
            .subscribe((url: string | null) => {
              if (url) {
                this.photoUrls[p.id] = this.sanitizer.bypassSecurityTrustUrl(url);
                this.cdr.detectChanges();
              }
            });
        }
      });
    }, () => {
      this.isLoading = false;
    });
  }

  getPhotoSrc(id: string): SafeUrl | string {
    return this.photoUrls[id] || 'assets/img/user/user1.jpg';
  }

  openPhotoPreview(id: string): void {
    this.previewPhotoSrc = this.getPhotoSrc(id);
  }

  closePhotoPreview(): void {
    this.previewPhotoSrc = null;
  }

  action(professionalId: string, status: string): void {
    this.snackbar.open('Atualizando...', 'OK', {
      duration: 3000,
    });

    this.professionalService.status(professionalId, status).subscribe(
      () => {
        this.snackbar.open('Atualizado com sucesso', 'OK', {
          duration: 3000,
        });
        this.getAll(this.statusFilter);
      },
      (error) =>
        this.snackbar.open(
          error.error.message ??
            'Não foi possível confirmar esse cadastro, tente novamente mais tarde!',
          'OK',
          { duration: 6000 }
        )
    );

  }

  getNameStatus(status: string) {
    switch(status) {
      case 'PENDING':
        return 'Pendente';
      case 'REJECTED':
        return 'Rejeitado'
      case 'CFM-VALIDATE':
        return 'CFM Validação';
      case 'APPROVED':
      default:
        return 'Aprovado';
    }
  }
}
